import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** How long the success toast stays on screen, in ms. */
const TOAST_DURATION = 3200;

/**
 * Upgrades a single-bottle cart to the "Buy 2, Get 1" bundle variant without
 * leaving the drawer.
 *
 * Fulfilment is keyed off bundle SKUs, so the upgrade must REPLACE the single
 * bottles rather than add to them — two Starter lines turning into two Starter
 * lines plus a bundle would ship two bottles when the customer expects three.
 * One `/cart/update.js` call zeroes every single-bottle variant and sets the
 * bundle to 1, atomically, so the cart can never land half-swapped. That single
 * call covers all three scenarios: one Starter, two Starters, or any other
 * Starter quantity all collapse to exactly one bundle line.
 *
 * TAG NAME: must end in `-component`. The theme's delegated `on:click`
 * dispatcher resolves handlers via `getClosestComponent()`, which only
 * recognises an element that is already an upgraded `Component` OR whose tag
 * ends in `-component`. Without the suffix an un-upgraded element is skipped and
 * the click silently resolves to the enclosing `cart-items-component`, which has
 * no `upgrade()` method.
 *
 * LOADING: this module is loaded from snippets/cart-drawer.liquid, above the
 * `data-hydration-key` boundary. It must never be script-tagged from inside the
 * upsell markup — morph() builds that subtree with DOMParser and inserted
 * <script> elements are inert, so the module would never run and the button
 * would do nothing.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [upgradeButton]
 * @property {HTMLElement} [errorMessage]
 *
 * @extends {Component<Refs>}
 */
class CartBundleUpsellComponent extends Component {
  /** @type {boolean} Guards against double submission from an impatient double-tap. */
  #pending = false;

  /** @returns {number[]} */
  get #singleVariantIds() {
    return (this.dataset.singleVariants ?? '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /** @returns {number | null} */
  get #bundleVariantId() {
    const id = Number(this.dataset.bundleVariant);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  /** @returns {string} */
  get #sectionId() {
    return this.dataset.sectionId || 'cart-drawer-section';
  }

  /**
   * Resolves the cart update endpoint, honouring locale-prefixed routes
   * (`/en/cart/update.js`) on multi-market storefronts.
   * @returns {string}
   */
  get #updateUrl() {
    const routes = /** @type {any} */ (window).Theme?.routes ?? /** @type {any} */ (window).Shopify?.routes ?? {};
    if (routes.cart_update_url) return routes.cart_update_url;
    const cartUrl = routes.cart_url || `${routes.root ?? '/'}cart`.replace('//', '/');
    return `${cartUrl}/update.js`;
  }

  /**
   * Swaps every single-bottle line for one bundle line.
   * Bound declaratively via `on:click="/upgrade"`.
   *
   * @param {Event} [event]
   */
  async upgrade(event) {
    event?.preventDefault();

    if (this.#pending) return;

    const bundleVariantId = this.#bundleVariantId;
    const singleVariantIds = this.#singleVariantIds;

    if (!bundleVariantId || singleVariantIds.length === 0) {
      console.warn('[cart-bundle-upsell] Missing variant configuration; ignoring upgrade.');
      return;
    }

    // Capture the dialog now. The morph below removes this component from the
    // DOM, so `this.closest()` would return null by the time we show the toast.
    const dialog = this.closest('dialog');

    this.#pending = true;
    this.#setBusy(true);
    this.#hideError();

    /** @type {Record<string, number>} */
    const updates = {};
    for (const id of singleVariantIds) updates[String(id)] = 0;
    updates[String(bundleVariantId)] = 1;

    // Refresh every cart section on the page, not just ours — on the cart page
    // the drawer and the main cart can both be mounted.
    const sectionsToUpdate = new Set([this.#sectionId]);
    for (const node of document.querySelectorAll('cart-items-component')) {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionsToUpdate.add(node.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    // Announce before the network call so the cart bubble, header count and
    // sticky ATC all shimmer in step with the drawer.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [{ id: String(bundleVariantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(
        this.#updateUrl,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );

      const payload = await response.json();

      if (!response.ok || payload.status || payload.errors) {
        throw new Error(payload.description || payload.message || 'Cart update failed');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(payload),
        detail: {
          sections: payload.sections,
          items: payload.items,
          itemCount: payload.item_count,
          source: 'cart-bundle-upsell',
          didError: false,
        },
      });

      // Morph in place. Replacing the section wholesale would tear down the
      // <theme-drawer> and slam the drawer shut mid-interaction.
      const html = payload.sections?.[this.#sectionId];
      if (html) {
        await morphSection(this.#sectionId, html, { mode: 'hydration' });
      }

      showUpgradeToast(dialog);
    } catch (error) {
      console.error('[cart-bundle-upsell] Upgrade failed:', error);
      deferred.reject(error instanceof Error ? error : new Error(String(error)));

      this.#showError();
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to upgrade cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      this.#pending = false;
      // The morph usually replaces this node outright; guard for the error path
      // where it survives and must become clickable again.
      if (this.isConnected) this.#setBusy(false);
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    const button = this.refs?.upgradeButton;
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
    this.classList.toggle('cart-upsell--busy', busy);
  }

  #showError() {
    const error = this.refs?.errorMessage;
    if (!(error instanceof HTMLElement)) return;
    error.hidden = false;
  }

  #hideError() {
    const error = this.refs?.errorMessage;
    if (!(error instanceof HTMLElement)) return;
    error.hidden = true;
  }
}

/**
 * Shows a transient confirmation inside the drawer.
 *
 * Mounted on the <dialog> itself — a sibling of <cart-drawer-component> — rather
 * than anywhere inside `data-hydration-key="cart-drawer-inner"`. The successful
 * upgrade removes the upsell card from the re-rendered cart (the cart now holds
 * a bundle), so a toast placed inside that subtree would be wiped by the very
 * morph it is meant to confirm.
 *
 * @param {HTMLDialogElement | null} dialog
 */
function showUpgradeToast(dialog) {
  if (!dialog?.isConnected) return;

  // Reuse the node if the customer somehow triggers two upgrades in a row, so
  // repeated calls can't stack toasts or leave orphaned timers behind.
  let toast = dialog.querySelector('.cart-upsell-toast');

  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.className = 'cart-upsell-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML =
      '<span class="cart-upsell-toast__check" aria-hidden="true">✓</span><span>Upgraded — 3 bottles, free shipping</span>';
    dialog.appendChild(toast);
  }

  const element = toast;
  const previousTimer = Number(element.dataset.hideTimer || 0);
  if (previousTimer) clearTimeout(previousTimer);

  requestAnimationFrame(() => element.classList.add('cart-upsell-toast--visible'));

  const timer = window.setTimeout(() => {
    element.classList.remove('cart-upsell-toast--visible');
    window.setTimeout(() => element.remove(), 300);
  }, TOAST_DURATION);

  element.dataset.hideTimer = String(timer);
}

if (!customElements.get('cart-bundle-upsell-component')) {
  customElements.define('cart-bundle-upsell-component', CartBundleUpsellComponent);
}
